import { Box, Flex, HStack, Img, Link, Stack, StackProps } from '@chakra-ui/react'
import { LandingContainer } from 'components/common/LandingContainer'
import { Body1, Body2, H2 } from 'components/typography'
import NextLink from 'next/link'
import { FC } from 'react'

import { OMENS_RECLAME_AQUI_PERIOD, OMENS_RECLAME_AQUI_URL } from '../../omensSeoTechnical/head'
import RichText from '../ProgramLanding/richText'
import { GEO_DOCTORS } from './doctors'
import { PRESS_LOGOS, PRESS_PATH } from './press'
import { GeoConditionContent } from './types'

/**
 * Morgan's adjustments to the validation copies (24/10/2026): GEO text visible and in the server HTML, but out of
 * the hero and away from the buttons; press logos and Reclame Aqui as links; the reviewer line at the end of the
 * medical content.
 */

const formatDate = (iso: string) => iso.split('-').reverse().join('/')

/**
 * The press logos, one per article, each a link to it (new tab) with "outlet: headline" as `alt`, and "Ver todas as
 * matérias" to /imprensa. `tone`: `mint` on the dark blocks, `dark` on the light ones.
 */
export const PressLogos: FC<{ tone: 'mint' | 'dark'; centered?: boolean } & StackProps> = ({
  tone,
  centered = false,
  ...props
}) => (
  <Stack spacing={4} align={centered ? 'center' : 'stretch'} {...props}>
    <Flex
      as="ul"
      listStyleType="none"
      wrap="wrap"
      align="center"
      justify={centered ? 'center' : 'flex-start'}
      gap={{ base: '1.25rem', lg: '2rem' }}
    >
      {PRESS_LOGOS.map(({ outlet, title, href, logo }) => (
        <Box as="li" key={href} lineHeight={0}>
          <a href={href} target="_blank" rel="noopener">
            <Img
              src={logo.src[tone]}
              alt={`${outlet}: ${title}`}
              htmlWidth={logo.width}
              htmlHeight={logo.height}
              loading="lazy"
              decoding="async"
            />
          </a>
        </Box>
      ))}
    </Flex>
    <Body2 color={tone === 'mint' ? 'magicmint' : 'darkchoc'}>
      <Link as={NextLink} href={PRESS_PATH} prefetch={false} textDecoration="underline">
        Ver todas as matérias
      </Link>
    </Body2>
  </Stack>
)

/**
 * Reclame Aqui (PRD 2.7, Morgan 24/10/2026): a compact strip, the whole strip a link to the Omens profile (the
 * `sameAs` of the Organization), the static seal (no widget script) and the 12-month window of
 * OMENS_RECLAME_AQUI_PERIOD. A light pill, so the seal reads the same on dark and light sections. `mini`: the
 * footer version.
 */
export const ReclameAquiStrip: FC<{ mini?: boolean } & StackProps> = ({ mini = false, ...props }) => {
  const sealHeight = mini ? 20 : 26
  return (
    <Stack align="center" w="full" {...props}>
      <Link
        href={OMENS_RECLAME_AQUI_URL}
        isExternal
        rel="noopener"
        display="inline-flex"
        alignItems="center"
        gap={mini ? 2 : 3}
        maxW="full"
        bg="alabaster"
        color="darkchoc"
        border="1px solid"
        borderColor="blackAlpha.200"
        rounded="full"
        px={mini ? 3 : 4}
        py={mini ? 1.5 : 2}
        _hover={{ textDecoration: 'none', borderColor: 'blackAlpha.400' }}
      >
        <Img
          src="/landings/reclame-aqui-otimo.webp"
          alt="Reclame Aqui: reputação Ótima"
          htmlWidth={Math.round((sealHeight * 392) / 129)}
          htmlHeight={sealHeight}
          loading="lazy"
          flexShrink={0}
        />
        {/* One flow: the text, then the period in smaller type (1 line when it fits, 2 on a phone) */}
        <Box as="span" lineHeight="1.3" textAlign="left" fontSize={mini ? '0.6875rem' : '0.8125rem'}>
          <Box as="span" fontWeight={700}>
            Reputação Ótima · 100% das reclamações respondidas
          </Box>{' '}
          <Box as="span" fontSize={mini ? '0.625rem' : '0.6875rem'} opacity={0.8} whiteSpace="nowrap">
            {formatDate(OMENS_RECLAME_AQUI_PERIOD.start)} a {formatDate(OMENS_RECLAME_AQUI_PERIOD.end)}
          </Box>
        </Box>
      </Link>
    </Stack>
  )
}

/**
 * "Revisado por Dr. …, urologista · CRM-SP … · RQE … · Atualizado em dd/mm/aaaa" (PRD 5.2), moved by Morgan
 * (24/10/2026) to the end of the medical content, between the FAQ and the references: a discreet line with the
 * doctor's small photo, the name linking to the author page. Only "Atualizado em" is shown (the JSON-LD keeps the
 * other dates).
 */
export const GeoReviewerLine: FC<{ byline: GeoConditionContent['byline'] }> = ({ byline }) => {
  const doctor = GEO_DOCTORS[byline.doctor]
  return (
    <HStack spacing={3} align="center">
      <Img
        src={doctor.avatar}
        alt={doctor.name}
        htmlWidth={40}
        htmlHeight={40}
        boxSize="40px"
        objectFit="cover"
        rounded="full"
        loading="lazy"
        flexShrink={0}
      />
      <Body2 color="gray.250">
        Revisado por{' '}
        <Link as={NextLink} href={doctor.profilePath} prefetch={false} rel="author" textDecoration="underline">
          {doctor.name}
        </Link>
        , {doctor.specialty} · {doctor.crm} · {doctor.rqe} · Atualizado em{' '}
        <time dateTime={byline.updated}>{formatDate(byline.updated)}</time>
      </Body2>
    </HStack>
  )
}

/**
 * The citable summary in its own section right after the hero (Morgan, 24/10/2026: GEO text out of the hero): H2,
 * body text at reading width, neutral background.
 */
export const GeoAboutBlock: FC<{ title: string; text: string }> = ({ title, text }) => (
  <Box as="section" bg="alabaster" w="full">
    <LandingContainer maxW="880px" py={{ base: '40px', lg: '64px' }} spacing={4}>
      <H2 fontSize={{ base: '1.75rem', lg: '2.25rem' }} lineHeight={{ base: '2.125rem', lg: '2.75rem' }}>
        {title}
      </H2>
      <Body1>
        <RichText text={text} />
      </Body1>
    </LandingContainer>
  </Box>
)
